import React, { useMemo, useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FilterTags } from 'src/components';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { filterAutocompleteStyle } from './autocomplete-filter-utils';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
}));

interface BoardFilterOption {
  __typename: 'Board';
  color: string;
  icon: string;
  id: string;
  name: string;
}

export function BoardAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const { data } = Apollo.useGetBoardsQuery();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<BoardFilterOption[]>(item.value ?? []);

  const options = useMemo(() => {
    const boards = data?.boards ?? [];
    return boards
      .map(board => ({
        __typename: 'Board' as const,
        color: board.color,
        icon: board.icon,
        id: board.id,
        name: board.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.boards]);

  function handleValueChange(newValue: BoardFilterOption[]): void {
    applyValue({ ...item, value: newValue });
    setValue(newValue);
  }

  return (
    <Autocomplete<BoardFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      options={options}
      renderInput={params => (
        <TextField placeholder="Search boards..." {...params} variant="standard" />
      )}
      renderOption={(props, option, state) => {
        const { className, ...otherProps } = props;
        return (
          <li {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <LucideIconRenderer color={option.color} icon={option.icon} size={16} />
            <Typography>{option.name}</Typography>
          </li>
        );
      }}
      renderValue={tags => (
        <FilterTags<BoardFilterOption>
          getTextFromTag={tag => tag.name}
          tags={tags}
          truncateTags={truncateTags}
          onDelete={tag => handleValueChange(tags.filter(t => t.id !== tag.id))}
        />
      )}
      slotProps={{
        popper: {
          modifiers: [
            { enabled: false, name: 'flip' },
            { enabled: false, name: 'preventOverflow' },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, newValue) => handleValueChange(newValue)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
    />
  );
}
